import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import {
  attachClosestEdge,
  type Edge,
  extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import SelectEntryMenu from '@databases/context-menu/select-entry-menu';
import { Position } from '@databases/context-menu/type';
import { useContextualMenu } from '@databases/contextual-menu/useContextualMenu';
import { aiFirstCreationStreamingState } from '@databases/database-ai-first-creation-state';
import {
  BORDER_BLUE,
  BORDER_GREEN,
  BORDER_ORANGE,
  BORDER_PURPLE,
  useDatabaseStreamState,
  useRovoGradientAnimationForTableView,
} from '@databases/database-ai-first-creation-ui';
import { DragType, setPublicDragData } from '@databases/drag';
import { PerformanceTimingManager } from '@databases/frontend-metrics/performance-timing-manager';
import { messages } from '@databases/intl/messages';
import Z_INDEX from '@databases/layout/z-index';
import { isNativeEmbedEditorPlugin } from '@databases/modes';
import { PluginKey } from '@databases/plugins';
import { isPluginEnabled } from '@databases/plugins/helper';
import { usePresence } from '@databases/presence';
import { isModifierKeyPressed, useHoverState } from '@databases/react';
import { type DefaultRootState, defaultStoreUtils } from '@databases/redux';
// eslint-disable-next-line no-restricted-imports -- TODO: migrate this selector usage to the SelectionsReader service contract.
import { selectIsEntireEntrySelected } from '@databases/redux/cellSelections/selectors';
import {
  calculate2DValueRange,
  calculateRangeFromFocus,
} from '@databases/redux/cellSelections/utils';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { getDatabaseWithCurrentView } from '@databases/redux/database-view';
import { getService } from '@databases/service-container';
import {
  IGNORED_TABLE_ELEMENT_CLASS,
  IGNORED_TABLE_ELEMENT_WIDTH_CLASS,
} from '@databases/table-resizer';
import { RIGHT_ACTIONS_MID_WIDTH } from '@databases/table-resizer/constants';
import {
  COMPACT_NUMBERED_COLUMN_WIDTH,
  NATIVE_EMBED_DRAG_HANDLE_SPACE,
  TABLE_CLASSNAMES,
} from '@databases/utils/constants';
import { blurActiveElement, removeTextSelection } from '@databases/utils/database/utils';
import { getIsSortingActive } from '@databases/utils/database/view/view';
import { useIsFeatureEnabled } from '@databases/utils/hooks/useIsFeatureEnabled';
import useView from '@databases/views/useView';
import { captureException } from '@sentry/react';
import composeRefs from '@seznam/compose-react-refs';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { useIntl } from 'react-intl-next';
import styled, { css } from 'styled-components';
import { useSnapshot } from 'valtio';

import { getExperimentValue } from '@canvas/client-feature-flags';
import { MetricName } from '@canvas/metrics';

import { getBodyFromTable, getRowsFromBody, getTableFromBodyRow } from '../../../table';
import { isApple, shouldShowEntryPrefix } from '../../../utils';

import EntryPrefix, { type EntryIsSelectedMap } from './EntryPrefix';
import RowDragHandle from './RowDragHandle';
import TableLayoutCell from './TableLayoutCell';
import TableLayoutCellWithMenu from './TableLayoutCellWithMenu';
import { buildEntryDragImageFromState } from './build-entry-drag-image-from-state';
import { DisabledTooltipContainer } from './disabled-tooltip-container';
import { STICKY_BOTTOM_ELEMENTS_HEIGHT } from './table-layout-row-constants';

const SELECT_BUTTON_PADDING = 9;
const SELECT_BUTTON_WIDTH = 15;
const NATIVE_EMBED_DRAG_IMAGE_OFFSET = 12;

type InsertRowIndicatorProps = {
  insertIndicatorPosition: Position | undefined;
  isNativeEmbedEditorPlugin: boolean;
};

const InsertRowIndicator = css<InsertRowIndicatorProps>`
  :after {
    content: ' ';
    position: absolute;
    left: 0;
    width: calc(
      100% -
        ${({ isNativeEmbedEditorPlugin }) =>
          1 + (isNativeEmbedEditorPlugin ? 0 : RIGHT_ACTIONS_MID_WIDTH)}px
    );
    height: 2px;
    background-color: ${colors.B200};
    opacity: 0;

    ${({ insertIndicatorPosition }) =>
      insertIndicatorPosition === Position.BEFORE
        ? // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
          css`
            opacity: 1;
            top: -0.5px;
          `
        : insertIndicatorPosition === Position.AFTER &&
          // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
          css`
            opacity: 1;
            bottom: -0.5px;
          `};
  }
`;

interface TrProps extends InsertRowIndicatorProps {
  isInitialized: boolean;
  isSortingActive: boolean;
  isNativeEmbedEditorPlugin: boolean;
  isEntryPrefixHovered: boolean;
  isSelected: boolean;
  isReadOnly: boolean;
  collaboratorColor?: string;
  isStreamingRovoPresence?: boolean;
  isFilterLoadingFadeRow?: boolean;
}

export const Tr = styled.tr<TrProps>`
  transition: opacity 1s;
  opacity: ${({ isInitialized }) => (isInitialized ? '1' : '0')};
  position: relative;
  --angle: 0deg;

  ${InsertRowIndicator};

  ${({ isFilterLoadingFadeRow }) =>
    isFilterLoadingFadeRow &&
    css`
      mask-image: linear-gradient(to bottom, #000 0%, rgba(0, 0, 0, 0.3) 100%);
    `}

  &.${TABLE_CLASSNAMES.SHOW_AS_DISABLED} {
    * {
      cursor: ${({ isSortingActive }) =>
        isSortingActive ? 'not-allowed' : 'grabbing'} !important;
    }
  }

  &:hover:not(.${TABLE_CLASSNAMES.SHOW_AS_DISABLED}) {
    .${TABLE_CLASSNAMES.SELECT_BUTTON} {
      display: flex;
    }
  }

  ${({ isEntryPrefixHovered, isSelected }) =>
    isEntryPrefixHovered &&
    !isSelected &&
    css`
      &:not(.${TABLE_CLASSNAMES.IS_SELECTED}) > td:not(.${IGNORED_TABLE_ELEMENT_CLASS}) {
        background-color: ${token('color.background.input.hovered', '#F8F8F8')};
      }
    `}
  td.${IGNORED_TABLE_ELEMENT_WIDTH_CLASS}:last-of-type {
    width: ${({ isNativeEmbedEditorPlugin }) => (isNativeEmbedEditorPlugin ? 0 : 9)}px;
  }

  ${({
    isSelected,
    collaboratorColor,
    isReadOnly,
    applyRovoGradientForTableRow,
    isSingleFieldStreamingForEdit,
  }) => {
    const borderColor =
      isSelected ||
      isReadOnly ||
      applyRovoGradientForTableRow ||
      isSingleFieldStreamingForEdit
        ? undefined
        : // eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
          collaboratorColor && token(collaboratorColor);

    if (applyRovoGradientForTableRow && !isSelected && !isReadOnly) {
      // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
      return css`
        /* animated gradient for the entire row */

        &::before {
          content: '';
          position: absolute;
          top: ${token('space.negative.025', '-2px')};
          bottom: ${token('space.negative.025', '-2px')};
          left: calc(var(--rovograd-left, 0px) - 2px);
          right: calc(var(--rovograd-right, 0px) - 2px);
          border-radius: ${token('radius.small', '3px')};
          padding: ${token('space.025', '2px')};
          background: conic-gradient(
            from var(--angle, 0deg),
            ${BORDER_ORANGE} 25%,
            ${BORDER_GREEN} 25% 50%,
            ${BORDER_BLUE} 50% 75%,
            ${BORDER_PURPLE} 75% 100%
          );
          -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          pointer-events: none;
          z-index: 300;
        }
      `;
    }

    return (
      borderColor &&
      css`
        > td:not(.${IGNORED_TABLE_ELEMENT_CLASS}) {
          border-block: ${token('border.width.selected', '2px')} solid ${borderColor} !important;
        }

        > td:nth-child(${() => (isNativeEmbedEditorPlugin() ? 1 : 2)}) {
          border-left: ${token('border.width.selected', '2px')} solid ${borderColor} !important;
          border-radius: ${token('radius.small', '3px')} 0 0
            ${token('radius.small', '3px')};
        }

        > td:not(.${IGNORED_TABLE_ELEMENT_CLASS}):nth-last-child(2) {
          border-right: ${token('border.width.selected', '2px')} solid ${borderColor};
          border-radius: 0 ${token('radius.small', '3px')} ${token('radius.small', '3px')}
            0;
        }
      `
    );
  }}
`;

export type DragState =
  | {
      type: 'idle';
    }
  | {
      type: 'dragging';
    }
  | {
      type: 'is-over';
      closestEdge: Edge | null;
    };

export interface TableLayoutRowProps {
  contentId: string;
  entryId: string;
  isContentEditable: boolean;
  canEditFields: boolean;
  canEditContent: boolean;
  editingIds: Set<{ entryId: string; fieldId: string | undefined }>;
  setEditingIds: React.Dispatch<
    React.SetStateAction<Set<{ entryId: string; fieldId: string | undefined }>>
  >;
  isDraggingActive: boolean;
  isFilterLoadingFadeRow?: boolean;
  isLastInColumn: boolean;
  onDragStart?: (e: React.DragEvent, entryId: string) => void;
  onDrag?: (e: React.DragEvent, entryId: string) => void;
  onDragEnd?: (e: React.DragEvent, entryId: string) => void;
  containerWidth: number;
  currentViewId: string;
  index: number;
  entryIsSelectedMap: EntryIsSelectedMap;
}

const TableLayoutRow = React.memo(
  React.forwardRef<HTMLElement, TableLayoutRowProps>(
    (
      {
        contentId,
        entryId,
        isContentEditable,
        canEditContent,
        canEditFields,
        editingIds,
        setEditingIds,
        isDraggingActive,
        isFilterLoadingFadeRow,
        isLastInColumn,
        onDragStart,
        containerWidth,
        currentViewId,
        index,
        entryIsSelectedMap,
      },
      ref
    ) => {
      const { view } = useView();
      const showEntryPrefix = shouldShowEntryPrefix(view);
      const viewSettings = view.settings;
      const { formatMessage } = useIntl();
      const [isHoveringRowDragHandle, hoverProps] = useHoverState();
      const [dragState, setDragState] = useState<DragState>({ type: 'idle' });
      const [isEntryPrefixHovered, setIsEntryPrefixHovered] = useState(false);

      const isCellSelectionEnabled = isPluginEnabled(PluginKey.CELL_SELECTION);

      const isSelectionsPerformanceEnabled = getExperimentValue(
        'databasesSelectionsPerformance',
        'isEnabled',
        { fireExperimentExposure: false }
      );

      const { recentCollaboratorColor } = usePresence({
        grouping: { entryId: entryId },
        componentId: 'TableLayoutRow',
        isEnabled: true,
      });

      const selectionsReader = getService('SelectionsReader');

      const [fieldIds, isSelected, isSortingActive, isFirstEntry, isLastEntry] =
        defaultStoreUtils.useEqualSelector((state: DefaultRootState) => {
          const database = selectDatabaseDraftByContentId(state, {
            contentId,
            viewSettings,
            includeDeleted: true,
          });
          const isSelected = selectIsEntireEntrySelected(state, {
            entryId,
          });
          const isSortingActive = getIsSortingActive(viewSettings);

          return [
            database.fields,
            isSelected,
            isSortingActive,
            database.entries.indexOf(entryId) === 0,
            database.entries.indexOf(entryId) === database.entries.length - 1,
          ] as const;
        });

      const [isInitialized, setIsInitialized] = useState(true);
      useEffect(() => {
        setIsInitialized(true);
      }, []);

      const store = defaultStoreUtils.useStore();
      const selectionsActions = getService('SelectionsActions');

      /**
       * Selection logic from Redux thunks `selectEntrySingle` and `selectEntryMulti`.
       *
       * - No modifier: selectEntrySingle — toggle: if only selected entry, deselect; else replace selection
       * - Shift: selectEntryMulti(isRangeSelect=true) — range select, adds to existing
       * - Shift+Meta/Ctrl: selectEntryMulti(isRangeSelect=false) — toggle individual entry in/out of selection
       */
      const handleSelectRow = useCallback(
        (e?: React.MouseEvent | React.KeyboardEvent) => {
          const isMultiSelectOn =
            !!e && isModifierKeyPressed(e, [isApple ? 'metaKey' : 'ctrlKey', 'shiftKey']);
          const isRangeSelectOn = !!e && isModifierKeyPressed(e, ['shiftKey']);
          blurActiveElement();
          removeTextSelection();

          const reader = getService('SelectionsReader');

          if (isRangeSelectOn) {
            if (!isMultiSelectOn) {
              const focusedItem = reader.getFocusedItem();
              const database = getDatabaseWithCurrentView(store.getState(), {
                contentId,
              });
              const entries = database?.entries ?? [];
              const focusedEntryId =
                focusedItem && 'entryId' in focusedItem ? focusedItem.entryId : undefined;
              const rangeEntryIds = calculateRangeFromFocus(
                entries,
                focusedEntryId,
                entryId
              );

              selectionsActions.selectEntries(rangeEntryIds, { clear: false });
            } else {
              const isSelected = reader.isEntrySelected(entryId);

              if (isSelected) {
                const selectedEntryIds = reader.getSelectedEntryIds();
                const remaining = selectedEntryIds.filter((id) => id !== entryId);

                if (remaining.length > 0) {
                  selectionsActions.selectEntries(remaining, {
                    clearSelectedValues: false,
                  });
                } else {
                  selectionsActions.deselectAll();
                }
              } else {
                selectionsActions.selectEntries([entryId], { clear: false });
              }
            }
          } else {
            const selectedEntryIds = reader.getSelectedEntryIds();

            if (selectedEntryIds.length === 1 && selectedEntryIds[0] === entryId) {
              selectionsActions.deselectAll();
            } else {
              selectionsActions.selectEntries([entryId]);
            }
          }

          selectionsActions.clearSelectionIntent();
        },
        [contentId, store, selectionsActions, entryId]
      );

      /**
       * Selection logic moved from Redux thunk `addEntryToValueRange`.
       *
       * Extends the current value selection to include all cells between the
       * currently selected values and the target entry. Used during fill-value dragging.
       */
      const handleSelectCells = useCallback(() => {
        const database = getDatabaseWithCurrentView(store.getState(), { contentId });

        if (!database) {
          return;
        }

        const reader = getService('SelectionsReader');
        const currentSelectedFieldIds = reader.getFieldIdsFromSelection();
        const currentSelectedEntryIds = reader.getEntryIdsFromSelection();

        if (currentSelectedFieldIds.length === 0) {
          return;
        }

        const values = calculate2DValueRange(
          database.entries,
          database.fields,
          currentSelectedEntryIds,
          currentSelectedFieldIds,
          entryId,
          currentSelectedFieldIds[0]
        );

        selectionsActions.selectValues(values);
      }, [store, contentId, entryId, selectionsActions]);

      const { closeMenu } = useContextualMenu();

      const dragImage = useRef<HTMLElement>();
      const trRef = useRef<HTMLTableRowElement>(null);
      const dragImageRootRef = useRef<Root>();

      const isDbAiFirstCreationEnabled = useIsFeatureEnabled('dbAiFirstCreation');
      const { isStreaming, operationType } = useDatabaseStreamState();
      const {
        entryIdSelectedForProcessing,
        currentEntryOperation,
        isSingleFieldStreaming,
      } = useSnapshot(aiFirstCreationStreamingState);
      const isEditStreaming = operationType === 'edit';
      const isStreamingRovoPresence =
        isStreaming &&
        isDbAiFirstCreationEnabled &&
        entryId === entryIdSelectedForProcessing;

      const isSingleFieldStreamingForEdit =
        operationType === 'edit' && !!isSingleFieldStreaming;
      const applyRovoGradientForTableRow =
        isStreamingRovoPresence && !isSingleFieldStreamingForEdit;

      // Animate rovo gradient border and compute insets
      useRovoGradientAnimationForTableView(
        trRef as React.RefObject<HTMLElement>,
        applyRovoGradientForTableRow
      );
      // Auto-scroll to streaming row
      useEffect(() => {
        if (
          !isStreaming ||
          !isDbAiFirstCreationEnabled ||
          entryId !== entryIdSelectedForProcessing ||
          !trRef.current
        ) {
          return;
        }

        const scrollableContainer = document.getElementById('scrollable-inner-container');
        if (!scrollableContainer || !trRef.current) {
          return;
        }

        const rowRect = trRef.current.getBoundingClientRect();
        const containerRect = scrollableContainer.getBoundingClientRect();
        const effectiveBottom = containerRect.bottom - STICKY_BOTTOM_ELEMENTS_HEIGHT;

        const isPartiallyHiddenBehindStickyElements = rowRect.bottom > effectiveBottom;
        const isAboveViewport = rowRect.bottom < containerRect.top;
        const isHidden = isPartiallyHiddenBehindStickyElements || isAboveViewport;

        if (isHidden) {
          requestAnimationFrame(() => {
            trRef.current?.scrollIntoView({
              behavior: 'instant',
              block: 'center',
            });
          });
        }
      }, [
        isStreaming,
        isDbAiFirstCreationEnabled,
        entryId,
        entryIdSelectedForProcessing,
      ]);

      /**
       * Select entry during AI first creation deletion.
       * Selection logic moved from Redux thunk `selectEntrySingle`.
       * When AI is deleting a row, we want to select it to show visual feedback.
       */
      useEffect(() => {
        const isAiFirstCreationDeletingRow =
          isStreaming &&
          isDbAiFirstCreationEnabled &&
          isEditStreaming &&
          currentEntryOperation === 'delete' &&
          entryId === entryIdSelectedForProcessing;

        if (isAiFirstCreationDeletingRow) {
          requestAnimationFrame(() => {
            selectionsActions.selectEntries([entryId]);
          });
        }
      }, [
        isStreaming,
        isDbAiFirstCreationEnabled,
        entryId,
        entryIdSelectedForProcessing,
        selectionsActions,
        isEditStreaming,
        currentEntryOperation,
      ]);

      useEffect(() => {
        PerformanceTimingManager.endMetricTiming({
          metricName: MetricName.DATABASE_ADD_ENTRY_TIME,
          id: entryId,
        });
      }, [entryId]);

      /**
       * Legacy drag image builder — finds selected rows via IS_SELECTED CSS class,
       * clones the entire tbody and filters.
       */
      const buildDragImageLegacy = useCallback(
        (e: React.DragEvent, selectedRows: string[]) => {
          const tr = e.target as HTMLTableRowElement;
          const table = getTableFromBodyRow(tr);
          const tbody = getBodyFromTable(table);
          const trs = getRowsFromBody(tbody);
          const selectedTrs = trs.filter((el) =>
            el.classList.contains(TABLE_CLASSNAMES.IS_SELECTED)
          );
          const currentIndexInSelectedTrs = selectedTrs.indexOf(trRef!.current!);
          const selectedTrsIndexOffsetHeight = selectedTrs
            .slice(0, currentIndexInSelectedTrs + 1)
            .reduce((p, c) => p + c.clientHeight, 0);

          const tBody = document.createElement('tbody');
          // clone the table element to get the table styles (but do not include descendants)
          const tableClone = table?.cloneNode(false) as HTMLTableElement;
          const tbodyClone = tbody?.cloneNode(true) as HTMLTableSectionElement;
          const clonedTrs = getRowsFromBody(tbodyClone);
          const clonedSelectedTrs = clonedTrs.filter((el) =>
            el.classList.contains(TABLE_CLASSNAMES.IS_SELECTED)
          ) as HTMLTableRowElement[];
          const originalWidths = Array.from(tr.cells).map((cell) => cell.offsetWidth);

          const dragImageWidth = originalWidths.reduce((sum, num) => sum + num, 0);
          const dragImageWidthForScrollableFields = containerWidth;

          const ignoredTrs = clonedSelectedTrs.flatMap(
            (el: HTMLElement) =>
              Array.from(
                el.querySelectorAll(`.${IGNORED_TABLE_ELEMENT_CLASS}`)
              ) as HTMLElement[]
          );

          const elementsToCollapse = isNativeEmbedEditorPlugin()
            ? ignoredTrs.filter((el) => !el.closest('.entry-number-table-data'))
            : ignoredTrs;

          elementsToCollapse.forEach((ignoredTr: HTMLElement) => {
            ignoredTr.style.width = '0';
          });

          if (isNativeEmbedEditorPlugin()) {
            clonedSelectedTrs.forEach((row: HTMLTableRowElement) => {
              const firstCell = row.cells[0];
              if (firstCell?.classList.contains(IGNORED_TABLE_ELEMENT_CLASS)) {
                Object.assign(firstCell.style, {
                  width: '0',
                  minWidth: '0',
                  maxWidth: '0',
                  padding: '0',
                  margin: '0',
                  overflow: 'hidden',
                });
              }
              const entryPrefixCell = row.querySelector(
                '.entry-number-table-data'
              ) as HTMLElement;
              if (entryPrefixCell) {
                entryPrefixCell.style.left = '0';
                entryPrefixCell.style.minWidth = `${
                  COMPACT_NUMBERED_COLUMN_WIDTH + NATIVE_EMBED_DRAG_HANDLE_SPACE
                }px`;
              }
            });
          }
          clonedSelectedTrs.forEach((el: HTMLElement) => {
            el.style.gridTemplateColumns = originalWidths.map((w) => `${w}px`).join(' ');
          });

          dragImage.current = document.createElement('div');
          table?.parentElement?.append(dragImage.current);
          const tableCloneEntryPrefix = clonedSelectedTrs[0]?.querySelector(
            '.entry-number-table-border'
          ) as HTMLElement;
          if (!tableCloneEntryPrefix) {
            captureException(
              new Error('Missing tableCloneEntryPrefix during drag operation'),
              {
                level: 'warning',
                tags: {
                  component: 'TableLayoutRow',
                  operation: 'handleDragStart',
                  issue: 'missing_entry_prefix_border',
                  captureLocation: 'table-layout-row-handle-drag-start',
                },
                extra: {
                  selectedRows,
                  selectedTrs,
                  clonedTrs,
                  clonedSelectedTrs,
                  selectedRowsCount: selectedRows.length,
                  selectedTrsCount: selectedTrs.length,
                  clonedTrsCount: clonedTrs.length,
                  clonedSelectedTrsCount: clonedSelectedTrs.length,
                  selectButton: clonedSelectedTrs
                    .find((el) => el.draggable)
                    ?.querySelector(`.${TABLE_CLASSNAMES.SELECT_BUTTON}`),
                  tableClone,
                  event: {
                    type: e.type,
                    target: e.target,
                    currentTarget: e.currentTarget,
                  },
                },
              }
            );
          } else {
            tableCloneEntryPrefix.style.marginTop = '0px';
          }
          tableClone.style.backgroundColor = 'transparent';
          tableClone.append(tBody);
          // eslint-disable-next-line canvas/no-spread-arguments
          tBody.append(...clonedSelectedTrs);
          dragImage.current.append(tableClone);
          if (!isSortingActive) {
            Object.assign(dragImage.current.style, {
              width: `${Math.min(dragImageWidth, dragImageWidthForScrollableFields)}px`,
              overflow: 'hidden',
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: Z_INDEX.DRAG_IMAGE_ELEMENT,
              paddingLeft: isNativeEmbedEditorPlugin()
                ? '0px'
                : `${SELECT_BUTTON_PADDING}px`,
            });
            e.dataTransfer.setDragImage(
              dragImage.current,
              isNativeEmbedEditorPlugin()
                ? NATIVE_EMBED_DRAG_IMAGE_OFFSET
                : SELECT_BUTTON_WIDTH / 2,
              selectedTrsIndexOffsetHeight - tr?.clientHeight / 2
            );
          } else {
            // Use a error message as the drag image when sorting is active
            dragImageRootRef.current = createRoot(dragImage.current);
            Object.assign(dragImage.current.style, {
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: Z_INDEX.DRAG_IMAGE_ELEMENT,
            });
            dragImageRootRef.current.render(
              <DisabledTooltipContainer isEntryCardActionBar={false}>
                <CrossCircleIcon label="" color={token('color.icon.danger', '#C9372C')} />
                <div>
                  {formatMessage(
                    messages['table-layout.reset-sorting-to-enable-row-dragging']
                  )}
                </div>
              </DisabledTooltipContainer>
            );
            e.dataTransfer.setDragImage(dragImage.current, 0, 25);
          }
        },
        [containerWidth, formatMessage, isSortingActive]
      );

      const handleDragStart = useCallback(
        (e: React.DragEvent) => {
          if (e.target !== e.currentTarget || !isHoveringRowDragHandle) {
            // Ignore drags that didn't originate from the row drag handle.
            // Pragmatic DnD sets draggable="true" on the <tr>, so the browser
            // fires dragstart for any drag within the row — including cell
            // selection drags. Only proceed for genuine row reorder drags.
            return;
          }
          removeTextSelection();
          closeMenu();

          const selectedRows = selectionsReader.getSelectedEntryIds();

          if (isSelectionsPerformanceEnabled) {
            buildEntryDragImageFromState({
              e,
              selectedRows,
              trRef,
              dragImageRef: dragImage,
              dragImageRootRef,
              containerWidth,
              isSortingActive,
              formatMessage,
            });
          } else {
            buildDragImageLegacy(e, selectedRows);
          }

          setPublicDragData(e.dataTransfer, {
            type: DragType.ENTRY_ROWS,
            contentId,
            draggingIds: selectedRows,
            dragOverId: entryId,
            height: trRef.current!.offsetHeight,
            width: trRef.current!.offsetWidth,
          });

          onDragStart?.(e, entryId);
        },
        [
          buildDragImageLegacy,
          closeMenu,
          containerWidth,
          contentId,
          entryId,
          formatMessage,
          isHoveringRowDragHandle,
          isSelectionsPerformanceEnabled,
          isSortingActive,
          onDragStart,
          selectionsReader,
        ]
      );

      const handleDragEnd = useCallback((e: React.DragEvent) => {
        if (e.target !== e.currentTarget) {
          // Ignore dragging descendent elements
          return;
        }

        if (dragImage.current) {
          dragImage.current.remove();
          dragImage.current = undefined;
        }

        if (dragImageRootRef.current) {
          dragImageRootRef.current.unmount();
          dragImageRootRef.current = undefined;
        }
      }, []);

      const showRowAsDisabled = isSelected && isDraggingActive;

      const [isHoverAddRowBeforeButton, addRowBeforeButtonHoverProps] = useHoverState();
      const [isHoverAddRowAfterButton, addRowAfterButtonHoverProps] = useHoverState();

      // Pragmatic drag and drop
      useEffect(() => {
        if (!canEditContent) {
          setDragState({ type: 'idle' });

          return;
        }

        if (!trRef.current) {
          return;
        }
        const element = trRef.current;
        const dragHandle =
          element.querySelector('[data-testid="row-select-button"]') || undefined;

        return combine(
          draggable({
            element,
            dragHandle,
            getInitialData() {
              return { type: DragType.ENTRY_ROWS, index, entryId };
            },
            onDragStart() {
              setDragState({ type: 'dragging' });
            },
            onDrop() {
              setDragState({ type: 'idle' });
            },
          }),
          dropTargetForElements({
            element,
            canDrop({ source }) {
              return (
                (source.data.type === DragType.ENTRY_ROWS &&
                  source.data.entryId !== entryId) ||
                source.data.type === DragType.FILL_VALUES
              );
            },
            getData({ input, element }) {
              const data = { entryId, index };
              return attachClosestEdge(data, {
                input,
                element,
                allowedEdges: ['top', 'bottom'],
              });
            },
            onDragEnter(args) {
              if (args.source.data.type === DragType.ENTRY_ROWS) {
                setDragState({
                  type: 'is-over',
                  closestEdge: extractClosestEdge(args.self.data),
                });
              } else if (args.source.data.type === DragType.FILL_VALUES) {
                const destination = args.location.current.dropTargets[0];

                if (destination) {
                  handleSelectCells();
                }
              }
            },
            onDrag(args) {
              if (args.source.data.type === DragType.ENTRY_ROWS) {
                const closestEdge: Edge | null = extractClosestEdge(args.self.data);

                // only update react state if the `closestEdge` changes
                setDragState((current) => {
                  if (current.type !== 'is-over') {
                    return current;
                  }
                  if (current.closestEdge === closestEdge) {
                    return current;
                  }
                  return {
                    type: 'is-over',
                    closestEdge,
                  };
                });
              }
            },
            onDragLeave() {
              setDragState({ type: 'idle' });
            },
            onDrop() {
              setDragState({ type: 'idle' });
            },
          })
        );
      }, [canEditContent, index, ref, entryId, handleSelectCells]);

      /**
       * We non longer need the TableLayoutCellWithMenu
       * when the experiment is enabled, as we have a context menu effect
       */
      const CellComponent = isSelectionsPerformanceEnabled
        ? TableLayoutCell
        : TableLayoutCellWithMenu;

      return (
        <Tr
          ref={composeRefs(ref, trRef)}
          data-row-index={index}
          data-entry-id={entryId}
          className={classNames({
            [TABLE_CLASSNAMES.IS_SELECTED]: isSelected,
            [TABLE_CLASSNAMES.SHOW_AS_DISABLED]: showRowAsDisabled,
          })}
          collaboratorColor={recentCollaboratorColor}
          draggable={canEditContent && isHoveringRowDragHandle}
          insertIndicatorPosition={
            isHoverAddRowBeforeButton
              ? Position.BEFORE
              : isHoverAddRowAfterButton
              ? Position.AFTER
              : undefined
          }
          isEntryPrefixHovered={isEntryPrefixHovered}
          isFilterLoadingFadeRow={isFilterLoadingFadeRow}
          isSortingActive={isSortingActive}
          isInitialized={isInitialized}
          isSelected={isSelected}
          isNativeEmbedEditorPlugin={isNativeEmbedEditorPlugin()}
          onDragEnd={handleDragEnd}
          onDragStart={handleDragStart}
          isReadOnly={!canEditContent}
          applyRovoGradientForTableRow={applyRovoGradientForTableRow}
          isSingleFieldStreamingForEdit={isSingleFieldStreamingForEdit}
        >
          {canEditContent &&
            showEntryPrefix &&
            !isNativeEmbedEditorPlugin() &&
            (isSelectionsPerformanceEnabled ? (
              <RowDragHandle entryId={entryId} hoverProps={hoverProps} />
            ) : (
              <SelectEntryMenu
                contentId={contentId}
                currentViewId={currentViewId}
                canEditContent={canEditContent}
                entryId={entryId}
                hoverProps={hoverProps}
                handleSelectEntry={handleSelectRow}
              />
            ))}
          <EntryPrefix
            contentId={contentId}
            entryId={entryId}
            entryIsSelectedMap={entryIsSelectedMap}
            index={index}
            isFirstEntry={isFirstEntry}
            isLastEntry={isLastEntry}
            isSelected={isSelected}
            setEntryHover={setIsEntryPrefixHovered}
            isReadOnly={!canEditContent}
            dragHandle={
              canEditContent && showEntryPrefix && isNativeEmbedEditorPlugin() ? (
                <SelectEntryMenu
                  contentId={contentId}
                  currentViewId={currentViewId}
                  canEditContent={canEditContent}
                  entryId={entryId}
                  hoverProps={hoverProps}
                  handleSelectEntry={handleSelectRow}
                  inlineTrigger
                />
              ) : undefined
            }
          />
          {fieldIds.map((fieldId, colIndex) => {
            return (
              <CellComponent
                key={fieldId}
                contentId={contentId}
                entryId={entryId}
                fieldId={fieldId}
                rowIndex={index}
                colIndex={colIndex}
                isContentEditable={isContentEditable}
                canEditContent={canEditContent}
                canEditFields={canEditFields}
                editingIds={editingIds}
                setEditingIds={setEditingIds}
                isFirstInRow={fieldIds[0] === fieldId}
                isLastInRow={fieldIds[fieldIds.length - 1] === fieldId}
                isLastInColumn={isLastInColumn}
                isSelectionDisabled={isDraggingActive || !isCellSelectionEnabled}
                isInsertContentDisabled={isDraggingActive}
                showAsDisabled={showRowAsDisabled}
                addRowBeforeButtonHoverProps={addRowBeforeButtonHoverProps}
                addRowAfterButtonHoverProps={addRowAfterButtonHoverProps}
                currentViewId={currentViewId}
                dropIndicatorEdge={
                  dragState.type === 'is-over' ? dragState.closestEdge : null
                }
                currentSortFieldId={viewSettings.sort?.fieldId}
              />
            );
          })}
          {
            <td
              className={classNames([TABLE_CLASSNAMES.ACTIONS], {
                [IGNORED_TABLE_ELEMENT_CLASS]: true,
                [IGNORED_TABLE_ELEMENT_WIDTH_CLASS]: true,
              })}
              aria-hidden="true"
            />
          }
        </Tr>
      );
    }
  )
);

export default TableLayoutRow;
